import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';

import { setPortal } from 'modules/portal';
import { onlyNumberRegexp } from 'lib/regexp/regexpConst';
import { makeRowColumn, getHeadObj, makeHeadData } from 'lib/commonLib';
import { paramConverter } from 'modules/common';
import { managerTableData, modifyManagerGoal } from 'lib/api/media';
import MbButton from 'components/atoms/MbButton';

import MbTooltip from 'components/atoms/MbTooltip';
import BasicTable from 'components/molecules/MbBasicTable';
import IconAreaWrap from 'components/molecules/MbTable/IconAreaWrap';
import SnackbarWrap from 'components/organisms/Snackbar/SnackbarWrap';
import FilterCollection from 'components/organisms/FilterCollection';

import GraphModal from './GraphModal';

const useStyles = makeStyles((theme) => ({
  title: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 1,
    '&> div, &>h6': {
      marginRight: 10,
    },
  },
  goalInput: {
    width: 120,
    '& input': {
      textAlign: 'right',
    },
  },
}));

const MediaManagerTable = () => {
  const { t } = useTranslation();
  const classes = useStyles();

  const initParam = {
    year: moment().format('Y'),
    month: moment().format('M'),
  };
  const [params, setParams] = useState(initParam);
  const [list, setList] = useState({
    year: [
      {
        value: moment().format('Y'),
        label: moment().format('Y') + t('년'),
      },
    ],
    month: [],
  });

  const tableHead = {
    headList: [
      {
        id: 'CONTACT_NAME',
        label: t('담당자명'),
        orderFlag: false,
        type: 'string',
        format: '@',
      },
      {
        id: 'PREV_MONTH_ADVRTS_AMT',
        label: t('전월 지출금액'),
        orderFlag: false,
        type: 'number',
        format: '#,##0',
      },
      {
        id: 'CUR_MONTH_GOAL',
        label: t('이달목표'),
        orderFlag: false,
        type: 'number',
        format: '#,##0',
      },
      {
        id: 'CUR_MONTH_ADVRTS_AMT',
        label: t('이달 지출금액'),
        orderFlag: false,
        type: 'number',
        format: '#,##0',
      },
      {
        id: 'GOAL_PER',
        label: t('도달율'),
        orderFlag: false,
        type: 'number',
        format: '0.00%',
      },
    ],
    sort: 'desc',
    order: 'STATS_DTTM',
  };
  const initTableList = [];
  const [tableList, setTableList] = useState(initTableList);
  const initLoading = {
    table: true,
  };
  const [loading, setLoading] = useState(initLoading);

  const initModal = {
    open: false,
    params: {},
  };
  const [modal, setModal] = useState(initModal);

  const initSnackbar = {
    open: false,
    variant: '',
    message: '',
  };
  const [snackbar, setSnackbar] = useState(initSnackbar);

  const dispatch = useDispatch();
  const { portal } = useSelector(({ portal }) => ({
    portal: portal,
  }));

  const handlePortalUrl = (url) => {
    let newPortal = portal.portal;
    let popIndex = newPortal.length;
    newPortal[popIndex] = { url: url, cnt: popIndex };
    dispatch(setPortal(newPortal));
  };

  useEffect(() => {
    let curYear,
      newYear = [...list.year];
    for (let i = 1; i < 10; i++) {
      curYear = {
        value: moment().add(-i, 'years').format('Y'),
        label: moment().add(-i, 'years').format('Y') + t('년'),
      };
      newYear.push(curYear);
    }
    let curMonth,
      newMonth = [...list.month];
    for (let i = 1; i < 13; i++) {
      curMonth = {
        value: i + '',
        label: i + t('월'),
      };
      newMonth.push(curMonth);
    }

    setList({
      ...list,
      year: newYear,
      month: newMonth,
    });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    getData();

    // eslint-disable-next-line
  }, [params]);

  const getData = () => {
    setLoading(initLoading);
    setTableList(initTableList);
    managerTableData({ ...paramConverter(params) })
      .then((response) => {
        const { data } = response.data;
        setTableList(data);
      })
      .catch((error) => {
        setSnackbar({
          ...snackbar,
          open: true,
          variant: 'error',
          message: error,
        });
      })
      .finally(() => {
        setLoading({
          ...loading,
          table: false,
        });
      });
  };

  const customized = {
    CONTACT_NAME: {
      makeBody: (rowObj, headsMap) => {
        const contactName = getHeadObj(headsMap, 'CONTACT_NAME');
        const showWindowUrl =
          '/report/media/mediatarget?contactNo=' +
          rowObj['CONTACT_NO'] +
          '&uri=/report/media/mediatarget&isPop=1';

        let content;
        if (rowObj['CONTACT_NAME'] === 'total') {
          content = (
            <>
              {makeRowColumn(
                rowObj['CONTACT_NAME'],
                contactName.type,
                contactName.format
              )}
            </>
          );
        } else {
          content = (
            <IconAreaWrap
              text={makeRowColumn(
                rowObj['CONTACT_NAME'],
                contactName.type,
                contactName.format
              )}
            >
              <MbTooltip
                title={t('담당자별 통계 팝업')}
                onClick={(e) => handlePortalUrl(showWindowUrl)}
              >
                <MbButton iconBtn icon="faExternalLink" />
              </MbTooltip>
              <MbTooltip
                title={t('담당자별 그래프')}
                onClick={(e) =>
                  handleModalOpen(rowObj['CONTACT_NAME'], rowObj['CONTACT_NO'])
                }
              >
                <MbButton iconBtn icon="faUserChart" />
              </MbTooltip>
            </IconAreaWrap>
          );
        }

        return content;
      },
    },
    CUR_MONTH_GOAL: {
      makeHead: (headsMap) => {
        return (
          <div style={{ width: 200 }}>
            {getHeadObj(headsMap, 'CUR_MONTH_GOAL').label}
          </div>
        );
      },
      makeBody: (rowObj, headsMap) => {
        const curMonthGoal = getHeadObj(headsMap, 'CUR_MONTH_GOAL');
        let content;
        if (rowObj['CONTACT_NAME'] === 'total') {
          content = (
            <>
              {makeRowColumn(
                rowObj['CUR_MONTH_GOAL'],
                curMonthGoal.type,
                curMonthGoal.format
              )}
            </>
          );
        } else {
          content = (
            <TextField
              variant="outlined"
              size="small"
              defaultValue={rowObj['CUR_MONTH_GOAL']}
              className={classes.goalInput}
              onKeyPress={(e) => handleGoalInput(e, rowObj)}
            />
          );
        }

        return content;
      },
    },
  };

  const handleGoalInput = (e, rowObj) => {
    const { value } = e.target;
    const { CONTACT_NO: contactNo, STATS_MTH: statsMth, GUBUN: gubun } = rowObj;
    if (!onlyNumberRegexp.test(value)) {
      setSnackbar({
        ...snackbar,
        open: true,
        variant: 'error',
        message: '목표금액은 숫자만 입력하세요.',
      });
    } else {
      if (e.charCode === 13) {
        modifyManagerGoal({ contactNo, goal: value, statsMth, gubun })
          .then((response) => {
            const { message } = response.data;
            setSnackbar({
              ...snackbar,
              open: true,
              variant: 'success',
              message: message,
            });
            getData();
          })
          .catch((error) => {
            setSnackbar({
              ...snackbar,
              open: true,
              variant: 'error',
              message: error,
            });
          });
      }
    }
  };

  /**
   * 스낵바 닫는 함수
   * 연속 클릭이 들어왔을 때는 기존 스낵바를 유지
   * @param event
   * @param reason
   */
  const handleSnackBarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({
      ...snackbar,
      open: false,
      message: '',
    });
  };

  /**
   * modal 열어주는 함수
   */
  const handleModalOpen = (name, no) => {
    let newModal = { ...initModal };
    newModal = {
      ...newModal,
      open: true,
      params: {
        name: name,
        contactNo: no,
      },
    };
    setModal({
      ...newModal,
    });
  };

  /**
   * 모달 닫아주는 함수
   */
  const handleModalClose = () => {
    let newModal = modal;
    newModal = {
      ...modal,
      open: false,
    };
    setModal(newModal);
  };

  /**
   * 페이지 파라미터를 관리하는 params을 수정하는 함수
   * @param newParams
   */
  const getParam = (newParams) => {
    if (JSON.stringify(params) !== JSON.stringify(newParams)) {
      setParams(newParams);
    }
  };

  return (
    <>
      <div className={classes.title}>
        <Typography variant="h6">{t('매체 담당자별 통계')}</Typography>
        <FilterCollection
          label=""
          name="year"
          params={params}
          value={params.year}
          type="select"
          list={list.year}
          callBack={getParam}
          labelKey="label"
          valueKey="value"
        />
        <FilterCollection
          label=""
          name="month"
          params={params}
          value={params.month}
          type="select"
          list={list.month}
          callBack={getParam}
          labelKey="label"
          valueKey="value"
        />
      </div>
      <BasicTable
        sort={tableHead.sort}
        order={tableHead.order}
        list={tableList}
        headCells={makeHeadData(tableHead.headList)}
        loading={loading.table}
        customized={customized}
        r
      />
      {modal.open && (
        <GraphModal
          modal={modal}
          params={params}
          handleModalClose={handleModalClose}
        />
      )}
      <SnackbarWrap
        open={snackbar.open}
        variant={snackbar.variant}
        message={snackbar.message}
        onClose={handleSnackBarClose}
      />
    </>
  );
};

export default MediaManagerTable;
